import { useState, useEffect } from 'react';
import { parsePlatformPaymentSettings, resolveInvoicePaymentMethods } from '@/lib/available-payment-methods';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { formatDate, formatCurrency, cn } from '@/lib/utils';
import { showSuccess, showError, showInfo, confirmAction } from '@/lib/swal';
import {
  saveSlickPayReturnOrigin,
  savePendingSlickPayOrder,
  slickPayInvoiceOriginKey,
  type SlickPayResultState,
} from '@/lib/slickpay-return';
import { readStoredGuestRsvpToken } from '@/lib/guest-rsvp-token';
import { Invoice, Event, RSVP } from '@/types';
import { CreditCard, Lock, CheckCircle, History, DollarSign, Building2, Copy, ArrowLeft, Clock, Smartphone, Wallet, Banknote, type LucideIcon } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import {
  collectionGroup,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  onSnapshot,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import type { PaymentMethodType } from '@masonic-tools/shared-types';
import { isSlickPayBankDetailsConfigured } from '@masonic-tools/shared-types';
import * as Sentry from '@sentry/react';
import { mapEventData, mapRsvpData } from '@/lib/event-utils';
import { BOOKING_FEE_LABEL, computeCreditRecoveryTotalPounds } from '@/lib/credit-booking-fee';
import { getCreditBookingFeePenceForEvent } from '@/lib/platform-booking-fee';
import {
  formatRsvpGuestName,
  describeRsvpTicketSelection,
  billableRsvpsForInvoicePayer,
  orderRsvpsForInvoice,
  ticketSubtotalForBreakdownRow,
  getHeadlinePayableDueForDashboard,
  countBookingFeeBillableSlots,
  sumBookingFeeBillableTicketSubtotal,
  isBookingFeeBillableInvoiceRow,
  perSlotBookingFeeForBillableTotals,
} from '@/lib/invoice-payment-breakdown';

function mapInvoiceFromPaymentSnapshot(
  snap: DocumentSnapshot,
  fallbackUnitId: string,
  fallbackEventId: string,
): Invoice {
  const invoiceData = snap.data() as Record<string, unknown>;
  return {
    id: snap.id,
    eventId: (invoiceData.eventId as string) || fallbackEventId,
    unitId: (invoiceData.unitId as string) || fallbackUnitId,
    lodgeId: invoiceData.lodgeId as Invoice['lodgeId'],
    ownerUid: invoiceData.ownerUid as string,
    totalAmount: Number(invoiceData.totalAmount) || 0,
    amountPaid: Number(invoiceData.amountPaid) || 0,
    outstandingBalance: Number(invoiceData.outstandingBalance) || 0,
    status: (invoiceData.status as Invoice['status']) || 'outstanding',
    rsvps: (invoiceData.rsvps as string[]) || [],
    paymentHistory: ((invoiceData.paymentHistory as any[]) || []).map((p: any) => ({
      ...p,
      date: p.date?.toDate ? p.date.toDate() : p.date instanceof Date ? p.date : new Date(p.date),
    })),
    paymentLink: (invoiceData.paymentLink as string) || '',
    remindersSent: ((invoiceData.remindersSent as any[]) || []).map((d: any) =>
      d?.toDate ? d.toDate() : d instanceof Date ? d : new Date(d),
    ),
    createdAt: (invoiceData.createdAt as { toDate?: () => Date })?.toDate?.()
      ? (invoiceData.createdAt as { toDate: () => Date }).toDate()
      : new Date(invoiceData.createdAt as string),
    updatedAt: (invoiceData.updatedAt as { toDate?: () => Date })?.toDate?.()
      ? (invoiceData.updatedAt as { toDate: () => Date }).toDate()
      : new Date(invoiceData.updatedAt as string),
    invoiceNumber: invoiceData.invoiceNumber as string | undefined,
    subtotal: invoiceData.subtotal as number | undefined,
    vatAmount: invoiceData.vatAmount as number | undefined,
    vatRate: invoiceData.vatRate as number | undefined,
    transactionFee: invoiceData.transactionFee as number | undefined,
    accountingTotals: invoiceData.accountingTotals as Invoice['accountingTotals'],
    allInPriceBreakdown: invoiceData.allInPriceBreakdown as Invoice['allInPriceBreakdown'],
    pendingVerificationAmount:
      invoiceData.pendingVerificationAmount !== undefined
        ? Number(invoiceData.pendingVerificationAmount)
        : undefined,
  };
}

// Declare SumUp SDK types
declare global {
  interface Window {
    SumUpCard?: {
      mount: (config: {
        checkoutId: string;
        showAmount?: boolean;
        showFooter?: boolean;
        locale?: string;
        onResponse?: (type: string, body: any) => void;
      }) => {
        unmount: () => void;
      };
    };
  }
}

interface SystemPaymentSettings {
  bankTransfer: {
    enabled: boolean;
    transactionFee: number;
  };
  stripe: {
    enabled: boolean;
    transactionFee: number;
  };
  sumup: {
    enabled: boolean;
    transactionFee: number;
  };
  slickpay: {
    enabled: boolean;
    transactionFee: number;
  };
}

const DEFAULT_SYSTEM_PAYMENT_SETTINGS: SystemPaymentSettings = {
  bankTransfer: { enabled: true, transactionFee: 0 },
  stripe: { enabled: true, transactionFee: 0 },
  sumup: { enabled: false, transactionFee: 0 },
  slickpay: { enabled: false, transactionFee: 0 },
};

type PaymentMethodMeta = {
  label: string;
  description: string;
  hero: string;
  icon: LucideIcon;
};

const PAYMENT_METHOD_META: Record<PaymentMethodType, PaymentMethodMeta> = {
  bank_transfer: {
    label: 'Bank Transfer',
    description: 'Pay via manual bank transfer with verification',
    hero: 'Complete your payment via bank transfer',
    icon: Building2,
  },
  stripe: {
    label: 'Stripe',
    description: 'Secure card payment via Stripe Checkout',
    hero: 'Complete your payment using Stripe Checkout',
    icon: CreditCard,
  },
  sumup: {
    label: 'SumUp',
    description: 'Card payment via SumUp',
    hero: 'Complete your payment using SumUp',
    icon: Smartphone,
  },
  slickpay: {
    label: 'Open Banking',
    description:
      'Pay directly from your bank via Open Banking. You will be redirected to a secure payment page, then returned here to confirm your payment.',
    hero: 'Complete your Open Banking payment',
    icon: Wallet,
  },
};

/**
 * Booking fee (credit cost recovery) is already in the invoice balance.
 * Online card "transaction fee" must not be stacked on top of it at checkout.
 */
/** Amount due for display and checkout; same rule as RSVP dashboard (live billable total minus effective paid). */
function getPayableOutstandingForInvoice(
  invoice: Invoice,
  breakdown: { event: Event; rsvps: RSVP[] } | null,
): number {
  return getHeadlinePayableDueForDashboard(invoice, breakdown);
}

function additiveProcessingFeeForCheckout(
  invoice: Invoice,
  method: PaymentMethodType,
  processingFees: Record<PaymentMethodType, number>
): number {
  const online = method === 'stripe' || method === 'sumup' || method === 'slickpay';
  if (!online) return 0;
  if ((invoice.allInPriceBreakdown?.creditCostRecovery ?? 0) > 0) return 0;
  return processingFees[method] ?? 0;
}

export default function PaymentPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();
  
  const isAdmin = userProfile?.isAdmin === true;
  
  // Admin-only logging helpers
  const adminLog = (...args: any[]) => {
    if (isAdmin) {
      console.log('[PAYMENT ADMIN]', ...args);
    }
  };
  
  const adminGroupLog = (label: string, data: any) => {
    if (!isAdmin) return;
    console.group(`[PAYMENT ADMIN] ${label}`);
    console.log(data);
    console.groupEnd();
  };
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [errorType, setErrorType] = useState<'not-found' | 'permission-denied' | 'voided' | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [fullPayment, setFullPayment] = useState(true);
  const [processingFees, setProcessingFees] = useState<Record<PaymentMethodType, number>>({
    bank_transfer: 0,
    stripe: 0,
    sumup: 0,
    slickpay: 0,
  });
  const [systemPaymentSettings, setSystemPaymentSettings] = useState<SystemPaymentSettings | null>(null);
  const [availableMethods, setAvailableMethods] = useState<PaymentMethodType[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType>('bank_transfer');
  const [unitBankDetails, setUnitBankDetails] = useState<{
    nameOnAccount?: string;
    sortCode?: string;
    accountNumber?: string;
  } | null>(null);
  const [sumupWidget, setSumupWidget] = useState<{
    checkoutId: string;
    widgetScriptUrl: string;
    totalAmount: number;
    show: boolean;
  } | null>(null);
  /** Event + RSVPs for per-guest invoice breakdown on the payment form. */
  const [paymentBreakdown, setPaymentBreakdown] = useState<{ event: Event; rsvps: RSVP[] } | null>(null);

  // Check if payment was canceled
  useEffect(() => {
    if (searchParams.get('canceled') === 'true') {
      showInfo('Payment was canceled. You can try again anytime.');
    }
  }, [searchParams]);

  // SlickPay Open Banking return: show toast on the invoice page after callback redirect
  useEffect(() => {
    const state = location.state as SlickPayResultState | null;
    if (!state?.slickpayResult) return;

    const message =
      state.message ||
      (state.slickpayResult === 'success'
        ? 'Your Open Banking payment has been confirmed.'
        : state.slickpayResult === 'pending'
          ? 'Your Open Banking payment is still confirming. Check back shortly.'
          : 'Your Open Banking payment did not complete. Please try again.');

    if (state.slickpayResult === 'success') {
      showSuccess(message);
    } else if (state.slickpayResult === 'pending') {
      showInfo(message);
    } else {
      showError(message);
    }

    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);

  // Load SumUp SDK and mount widget when sumupWidget is set
  useEffect(() => {
    if (!sumupWidget?.show || !sumupWidget.checkoutId) return;

    let scriptLoaded = false;
    let widgetInstance: any = null;

    const loadSumUpSDK = async () => {
      // Check if script is already loaded
      if (window.SumUpCard) {
        scriptLoaded = true;
        mountWidget();
        return;
      }

      // Load the SDK script
      const script = document.createElement('script');
      script.src = sumupWidget.widgetScriptUrl;
      script.async = true;
      
      script.onload = () => {
        adminLog('SumUp SDK loaded successfully');
        scriptLoaded = true;
        mountWidget();
      };

      script.onerror = () => {
        adminLog('ERROR: Failed to load SumUp SDK', { url: sumupWidget.widgetScriptUrl });
        showError('Failed to load SumUp payment widget. Please try again or use a different payment method.');
        setSumupWidget(null);
      };

      document.head.appendChild(script);
    };

    const mountWidget = () => {
      if (!window.SumUpCard || !sumupWidget.checkoutId) {
        adminLog('ERROR: SumUpCard not available or checkoutId missing');
        return;
      }

      const widgetContainer = document.getElementById('sumup-widget-container');
      if (!widgetContainer) {
        adminLog('ERROR: Widget container not found');
        return;
      }

      // Clear any existing widget
      widgetContainer.innerHTML = '';
      
      // Create new container for the widget - SumUp expects id="sumup-card"
      const container = document.createElement('div');
      container.id = 'sumup-card';
      container.className = 'w-full';
      widgetContainer.appendChild(container);

      try {
        adminLog('Mounting SumUp widget', { checkoutId: sumupWidget.checkoutId });
        
        widgetInstance = window.SumUpCard.mount({
          checkoutId: sumupWidget.checkoutId,
          showAmount: true,
          showFooter: true,
          locale: 'en-GB',
          onResponse: async (type: string, body: any) => {
            // STEP 1: Widget callback is utilized first
            adminLog('SumUp widget response received', { 
              type, 
              body,
              checkoutId: sumupWidget.checkoutId,
              invoiceId,
              timestamp: new Date().toISOString(),
            });
            
            // Handle all widget response types according to SumUp documentation
            if (type === 'sent') {
              // Form submitted to server - wait for final result
              adminLog('Payment form submitted, waiting for processing', { type, body });
              // Don't do anything yet - wait for success/failure/error response
              return;
            }
            
            if (type === 'invalid') {
              // Validation errors in the form
              adminLog('Payment form validation errors', { type, body });
              showError(body?.message || 'Please check your payment details and try again.');
              setSumupWidget(null);
              return;
            }
            
            if (type === 'auth-screen') {
              // User prompted for 3D Secure authentication
              adminLog('3D Secure authentication required', { type, body });
              showInfo('Please complete the authentication process.');
              // Don't unmount widget - user needs to complete authentication
              return;
            }
            
            if (type === 'success') {
              // STEP 2: Widget indicates success - now verify on server
              // According to SumUp docs: "success does not always mean the transaction was successful"
              // We MUST verify the checkout status on the server
              adminLog('Widget reported success - initiating server-side verification', {
                checkoutId: sumupWidget.checkoutId,
                invoiceId,
                body,
                note: 'Widget success does not guarantee payment - verifying on server',
              });
              
              try {
                // STEP 3: Server-side verification via backend function
                const handleCallback = httpsCallable(functions, 'handleSumUpCallback');
                
                adminLog('Calling server-side verification function', {
                  invoiceId,
                  checkoutId: sumupWidget.checkoutId,
                });
                
                const result = await handleCallback({
                  invoiceId,
                  checkoutId: sumupWidget.checkoutId,
                });
                
                const resultData = result.data as {
                  success: boolean;
                  status: string;
                  amount?: number;
                  newStatus?: string;
                  transactionId?: string;
                  transactionCode?: string;
                  error?: string;
                };
                
                adminLog('Server-side verification completed', { 
                  result: resultData,
                  verified: resultData.success,
                  status: resultData.status,
                });
                
                // STEP 4: Verify the server confirmed payment was successful
                if (resultData.success && resultData.status === 'PAID') {
                  // Payment verified as successful on server
                  adminLog('Payment verified as PAID by server', {
                    transactionId: resultData.transactionId,
                    transactionCode: resultData.transactionCode,
                    amount: resultData.amount,
                    invoiceStatus: resultData.newStatus,
                  });
                  
                  // Redirect to success page with transaction details
                  const successParams = new URLSearchParams({
                    invoice_id: invoiceId || '',
                    checkout_id: sumupWidget.checkoutId,
                    ...(resultData.transactionId && { transaction_id: resultData.transactionId }),
                    ...(resultData.transactionCode && { transaction_code: resultData.transactionCode }),
                  });
                  
                  navigate(`/payment/success?${successParams.toString()}`);
                } else if (resultData.status === 'PENDING') {
                  // Payment still pending - redirect to callback page for polling
                  adminLog('Payment status is PENDING - redirecting to callback page for verification', {
                    checkoutId: sumupWidget.checkoutId,
                  });
                  navigate(`/payment/${invoiceId}/sumup-callback?checkout_id=${sumupWidget.checkoutId}`);
                } else if (resultData.status === 'FAILED') {
                  // Payment failed according to server
                  adminLog('Payment failed according to server verification', {
                    status: resultData.status,
                    error: resultData.error,
                  });
                  showError(resultData.error || 'Payment failed. Please try again.');
                  setSumupWidget(null);
                } else {
                  // Unknown status from server
                  adminLog('Unknown payment status from server', {
                    status: resultData.status,
                    error: resultData.error,
                  });
                  showError(resultData.error || 'Payment status unknown. Please contact support if you completed the payment.');
                  setSumupWidget(null);
                }
              } catch (error: any) {
                adminLog('ERROR: Server-side verification failed', { 
                  error: error.message,
                  code: error.code,
                  details: error.details,
                  stack: error.stack,
                });
                console.error('Error processing payment callback:', error);
                
                // Show specific error messages
                let errorMessage = 'Payment verification failed. ';
                if (error.code === 'functions/not-found') {
                  errorMessage += 'Invoice not found. Please verify your payment link.';
                } else if (error.code === 'functions/failed-precondition') {
                  errorMessage += 'Payment configuration error. Please contact support.';
                } else if (error.code === 'functions/internal') {
                  errorMessage += 'Unable to verify payment status. Please contact support if you completed the payment.';
                } else {
                  errorMessage += error.message || 'Please contact support if you completed the payment.';
                }
                
                showError(errorMessage);
                
                // Redirect to callback page as fallback - it can retry verification
                navigate(`/payment/${invoiceId}/sumup-callback?checkout_id=${sumupWidget.checkoutId}`);
              }
            } else if (type === 'failure' || type === 'error') {
              // Payment failed according to widget
              adminLog('Widget reported payment failure', { type, body });
              showError(body?.message || 'Payment failed. Please try again.');
              setSumupWidget(null);
            } else if (type === 'cancel') {
              // User canceled the payment
              adminLog('Payment canceled by user', { type, body });
              showInfo('Payment was canceled.');
              setSumupWidget(null);
            } else {
              // Unknown response type
              adminLog('Unknown widget response type', { type, body });
              showError('Unexpected payment response. Please contact support if you completed the payment.');
              setSumupWidget(null);
            }
          },
        });

        adminLog('SumUp widget mounted successfully');
      } catch (error) {
        adminLog('ERROR: Failed to mount SumUp widget', { error });
        showError('Failed to initialize payment widget. Please try again.');
        setSumupWidget(null);
      }
    };

    loadSumUpSDK();

    // Cleanup function
    return () => {
      if (widgetInstance && typeof widgetInstance.unmount === 'function') {
        try {
          widgetInstance.unmount();
          adminLog('SumUp widget unmounted');
        } catch (error) {
          adminLog('Error unmounting SumUp widget', { error });
        }
      }
    };
  }, [sumupWidget, invoiceId, adminLog]);

  useEffect(() => {
    const fetchInvoiceData = async () => {
      setPaymentBreakdown(null);
      if (!invoiceId) {
        showError('Invalid payment link');
        setLoading(false);
        return;
      }

      try {
        // Load system payment settings
        let paymentSettings: SystemPaymentSettings = { ...DEFAULT_SYSTEM_PAYMENT_SETTINGS };
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          paymentSettings = parsePlatformPaymentSettings(settingsDoc.data());
        }
        setSystemPaymentSettings(paymentSettings);
        // Use the transaction fees from settings directly
        // The paymentSettings object already has the correct fallback logic built in
        setProcessingFees({
          bank_transfer: paymentSettings.bankTransfer.transactionFee,
          stripe: paymentSettings.stripe.transactionFee,
          sumup: paymentSettings.sumup.transactionFee,
          slickpay: paymentSettings.slickpay.transactionFee,
        });

        let invoiceDoc: any = null;
        let unitId = '';
        let eventId = '';
        let foundRsvpButCannotReadInvoice = false; // Track if we found RSVP but couldn't read invoice (permission issue)

        // Guests can no longer read RSVPs/invoices directly under the tightened security rules, and
        // the public payment route only carries the invoice id. Resolve the invoice entirely
        // server-side via the getInvoiceForPayment callable, which returns a non-PII summary keyed by
        // the unguessable id and locates the unit/event path. Authenticated owners/admins keep using
        // the direct collection-group reads below (which the rules still permit for them); signed-in
        // users paying an invoice they don't own (e.g. a shared link) fall back to the callable too.
        const resolveInvoiceViaCallable = async () => {
          try {
            const getInvoiceFn = httpsCallable(functions, 'getInvoiceForPayment');
            const guestRes: any = await getInvoiceFn({ invoiceId });
            const summary = guestRes?.data?.invoice;
            if (summary) {
              const gUnitId = summary.unitId || '';
              const gEventId = summary.eventId || '';
              invoiceDoc = {
                id: summary.id || invoiceId,
                exists: () => true,
                data: () => ({
                  ...summary,
                  ownerUid: '',
                  paymentHistory: [],
                  remindersSent: [],
                }),
                ref: { path: `units/${gUnitId}/events/${gEventId}/invoices/${summary.id || invoiceId}` },
              };
            }
          } catch (guestErr: any) {
            adminLog('Guest invoice resolution failed', { error: guestErr?.message, code: guestErr?.code });
          }
        };

        if (!currentUser) {
          await resolveInvoiceViaCallable();
        } else try {
          // For admins, try querying all invoices first (they have access to everything)
          // For regular users, try ownerUid filter first for better performance
          if (isAdmin && currentUser) {
            adminLog('Admin user detected - querying all invoices');
            try {
              // Admins can query all invoices - try this first
              const allInvoicesQuery = query(collectionGroup(db, 'invoices'));
              const allInvoicesSnapshot = await getDocs(allInvoicesQuery);
              invoiceDoc = allInvoicesSnapshot.docs.find(doc => doc.id === invoiceId);
              
              if (invoiceDoc) {
                adminLog('Found invoice via admin query (all invoices)');
              }
            } catch (adminError: any) {
              adminLog('Error querying all invoices as admin', {
                error: adminError.message,
                code: adminError.code,
              });
              // For admins, if collectionGroup fails, immediately try RSVP fallback
              // This is more reliable than trying other collectionGroup queries
              if (adminError.code === 'permission-denied' || adminError.code === 'failed-precondition' || adminError.message?.includes('index')) {
                adminLog('Admin collectionGroup query failed, trying RSVP-based fallback immediately');
                try {
                  // Query RSVPs by invoiceId - RSVPs are readable by anyone
                  const rsvpsQuery = query(
                    collectionGroup(db, 'rsvps'),
                    where('invoiceId', '==', invoiceId)
                  );
                  
                  const rsvpsSnapshot = await getDocs(rsvpsQuery);
                  
                  if (!rsvpsSnapshot.empty) {
                    const rsvpDoc = rsvpsSnapshot.docs[0];
                    const pathParts = rsvpDoc.ref.path.split('/');
                    const foundUnitId = pathParts[1];
                    const foundEventId = pathParts[3];
                    
                    adminLog('Found RSVP with invoiceId, extracting path', {
                      unitId: foundUnitId,
                      eventId: foundEventId,
                    });
                    
                    const directInvoiceRef = doc(
                      db,
                      'units',
                      foundUnitId,
                      'events',
                      foundEventId,
                      'invoices',
                      invoiceId
                    );
                    try {
                      const directInvoiceDoc = await getDoc(directInvoiceRef);
                      
                      if (directInvoiceDoc.exists()) {
                        invoiceDoc = directInvoiceDoc;
                        unitId = foundUnitId;
                        eventId = foundEventId;
                        adminLog('Found invoice via RSVP-based lookup (admin fallback)', {
                          unitId: foundUnitId,
                          eventId: foundEventId,
                        });
                      }
                    } catch (invoiceReadError: any) {
                      // If we found an RSVP but can't read the invoice, it's likely a permission issue
                      if (invoiceReadError.code === 'permission-denied') {
                        foundRsvpButCannotReadInvoice = true;
                        adminLog('Found RSVP but cannot read invoice - permission denied (admin fallback)', {
                          unitId: foundUnitId,
                          eventId: foundEventId,
                          error: invoiceReadError.message,
                        });
                      } else {
                        adminLog('Error reading invoice after finding RSVP (admin fallback)', {
                          error: invoiceReadError.message,
                          code: invoiceReadError.code,
                        });
                      }
                    }
                  }
                } catch (rsvpError: any) {
                  adminLog('RSVP-based fallback failed for admin', {
                    error: rsvpError.message,
                    code: rsvpError.code,
                  });
                }
              }
              // Continue to try other methods if RSVP fallback didn't work
            }
          }
          
          // Try collection group query - if ownerUid is available, filter by it for better performance
          // Skip this for admins since we already tried querying all invoices
          if (!invoiceDoc && currentUser && !isAdmin) {
            try {
              const invoicesQuery = query(
                collectionGroup(db, 'invoices'),
                where('ownerUid', '==', currentUser.uid)
              );
              
              const invoicesSnapshot = await getDocs(invoicesQuery);
              
              // Find the invoice with matching ID
              invoiceDoc = invoicesSnapshot.docs.find(doc => doc.id === invoiceId);
            } catch (error: any) {
              adminLog('Error querying invoices with ownerUid filter', {
                error: error.message,
                code: error.code,
              });
              // Continue to try without filter
            }
          }
          
          // If not found with ownerUid filter, try RSVP-based lookup for guest invoices
          // Guest invoices might have undefined/null/empty ownerUid, so we can't reliably query by ownerUid
          // Instead, find the invoice via RSVP lookup (works for non-logged-in users)
          if (!invoiceDoc) {
            try {
              adminLog('Trying RSVP-based lookup for guest invoices');
              // Query RSVPs by invoiceId - RSVPs are readable by anyone (including non-logged-in users)
              const rsvpsQuery = query(
                collectionGroup(db, 'rsvps'),
                where('invoiceId', '==', invoiceId)
              );
              
              const rsvpsSnapshot = await getDocs(rsvpsQuery);
              
              if (!rsvpsSnapshot.empty) {
                // Found an RSVP with this invoiceId - extract unitId and eventId from path
                const rsvpDoc = rsvpsSnapshot.docs[0];
                const pathParts = rsvpDoc.ref.path.split('/');
                // Path format: units/{unitId}/events/{eventId}/rsvps/{rsvpId}
                const foundUnitId = pathParts[1];
                const foundEventId = pathParts[3];
                
                adminLog('Found RSVP with invoiceId, extracting path for guest invoice', {
                  unitId: foundUnitId,
                  eventId: foundEventId,
                });
                
                // Now get the invoice directly using the known path
                const directInvoiceRef = doc(
                  db,
                  'units',
                  foundUnitId,
                  'events',
                  foundEventId,
                  'invoices',
                  invoiceId
                );
                try {
                  const directInvoiceDoc = await getDoc(directInvoiceRef);
                  
                  if (directInvoiceDoc.exists()) {
                    invoiceDoc = directInvoiceDoc;
                    unitId = foundUnitId;
                    eventId = foundEventId;
                    adminLog('Found guest invoice via RSVP-based lookup', {
                      unitId: foundUnitId,
                      eventId: foundEventId,
                    });
                  }
                } catch (invoiceReadError: any) {
                  // If we found an RSVP but can't read the invoice, it's likely a permission issue
                  // This happens when non-logged-in users try to access registered user invoices
                  // However, if the invoice is a guest invoice (no ownerUid), it should be readable
                  // So we should still try to proceed if this might be a guest invoice
                  if (invoiceReadError.code === 'permission-denied') {
                    foundRsvpButCannotReadInvoice = true;
                    adminLog('Found RSVP but cannot read invoice - permission denied', {
                      unitId: foundUnitId,
                      eventId: foundEventId,
                      error: invoiceReadError.message,
                      note: 'This may be a registered user invoice requiring authentication',
                    });
                  } else {
                    adminLog('Error reading invoice after finding RSVP', {
                      error: invoiceReadError.message,
                      code: invoiceReadError.code,
                    });
                    // For non-permission errors, don't mark as permission issue - might be transient
                  }
                }
              } else {
                adminLog('No RSVP found with this invoiceId');
              }
            } catch (guestError: any) {
              adminLog('Error in RSVP-based lookup for guest invoices', {
                error: guestError.message,
                code: guestError.code,
              });
              // Continue to try querying all invoices
            }
          }
          
          // If still not found, try querying all invoices (for shared payment links)
          // Note: This may fail for non-logged-in users if security rules restrict access
          // Skip for admins since we already tried this
          if (!invoiceDoc && !isAdmin) {
            try {
              // Query all invoices - this will require a composite index
              // Firestore will provide a link if index is missing
              // This might fail for non-logged-in users if rules don't allow reading all invoices
              const allInvoicesQuery = query(collectionGroup(db, 'invoices'));
              const allInvoicesSnapshot = await getDocs(allInvoicesQuery);
              invoiceDoc = allInvoicesSnapshot.docs.find(doc => doc.id === invoiceId);
            } catch (error: any) {
              adminLog('Error querying all invoices (collectionGroup)', {
                error: error.message,
                code: error.code,
                details: error.details,
              });
              
              // Fallback: Try to find invoice by searching RSVPs first (works for non-logged-in users)
              // RSVPs are publicly readable and contain invoiceId, so we can find the path this way
              if (error.code === 'failed-precondition' || error.message?.includes('index') || error.code === 'permission-denied') {
                adminLog('CollectionGroup query failed, trying RSVP-based fallback method');
                
                try {
                  // Query RSVPs by invoiceId - RSVPs are readable by anyone (including non-logged-in users)
                  // This works even when collectionGroup index is missing
                  const rsvpsQuery = query(
                    collectionGroup(db, 'rsvps'),
                    where('invoiceId', '==', invoiceId)
                  );
                  
                  const rsvpsSnapshot = await getDocs(rsvpsQuery);
                  
                  if (!rsvpsSnapshot.empty) {
                    // Found an RSVP with this invoiceId - extract unitId and eventId from path
                    const rsvpDoc = rsvpsSnapshot.docs[0];
                    const pathParts = rsvpDoc.ref.path.split('/');
                    // Path format: units/{unitId}/events/{eventId}/rsvps/{rsvpId}
                    const foundUnitId = pathParts[1];
                    const foundEventId = pathParts[3];
                    
                    adminLog('Found RSVP with invoiceId, extracting path', {
                      unitId: foundUnitId,
                      eventId: foundEventId,
                    });
                    
                    // Now get the invoice directly using the known path
                    const directInvoiceRef = doc(
                      db,
                      'units',
                      foundUnitId,
                      'events',
                      foundEventId,
                      'invoices',
                      invoiceId
                    );
                    try {
                      const directInvoiceDoc = await getDoc(directInvoiceRef);
                      
                      if (directInvoiceDoc.exists()) {
                        invoiceDoc = directInvoiceDoc;
                        unitId = foundUnitId;
                        eventId = foundEventId;
                        adminLog('Found invoice via RSVP-based lookup', {
                          unitId: foundUnitId,
                          eventId: foundEventId,
                        });
                      }
                    } catch (invoiceReadError: any) {
                      // If we found an RSVP but can't read the invoice, it's likely a permission issue
                      // This happens when non-logged-in users try to access registered user invoices
                      if (invoiceReadError.code === 'permission-denied') {
                        foundRsvpButCannotReadInvoice = true;
                        adminLog('Found RSVP but cannot read invoice - permission denied', {
                          unitId: foundUnitId,
                          eventId: foundEventId,
                          error: invoiceReadError.message,
                        });
                      } else {
                        adminLog('Error reading invoice after finding RSVP', {
                          error: invoiceReadError.message,
                          code: invoiceReadError.code,
                        });
                      }
                    }
                  } else {
                    adminLog('No RSVP found with this invoiceId');
                  }
                } catch (rsvpFallbackError: any) {
                  adminLog('RSVP-based fallback also failed', {
                    error: rsvpFallbackError.message,
                    code: rsvpFallbackError.code,
                  });
                  
                  // Last resort: Try direct path lookup if user has permission to query units
                  // This only works for authenticated users or if units collection is publicly readable
                  if (currentUser) {
                    try {
                      adminLog('Trying direct path lookup as last resort (authenticated users only)');
                      const unitsSnapshot = await getDocs(collection(db, 'units'));
                      const unitsToSearch = unitsSnapshot.docs.slice(0, 50);
                      
                      for (const unitDoc of unitsToSearch) {
                        const currentUnitId = unitDoc.id;
                        const eventsSnapshot = await getDocs(
                          collection(db, 'units', currentUnitId, 'events')
                        );
                        const eventsToSearch = eventsSnapshot.docs.slice(0, 20);
                        
                        for (const eventDoc of eventsToSearch) {
                          const currentEventId = eventDoc.id;
                          
                          try {
                            const directInvoiceRef = doc(
                              db,
                              'units',
                              currentUnitId,
                              'events',
                              currentEventId,
                              'invoices',
                              invoiceId
                            );
                            const directInvoiceDoc = await getDoc(directInvoiceRef);
                            
                            if (directInvoiceDoc.exists()) {
                              invoiceDoc = directInvoiceDoc;
                              unitId = currentUnitId;
                              eventId = currentEventId;
                              adminLog('Found invoice via direct path lookup (authenticated fallback)', {
                                unitId: currentUnitId,
                                eventId: currentEventId,
                              });
                              break;
                            }
                          } catch (directError) {
                            continue;
                          }
                        }
                        
                        if (invoiceDoc) break;
                      }
                    } catch (directFallbackError: any) {
                      adminLog('Direct path lookup fallback also failed', {
                        error: directFallbackError.message,
                      });
                    }
                  }
                }
              }
            }
          }
        } catch (error: any) {
          adminLog('Unexpected error during invoice lookup', {
            error: error.message,
            code: error.code,
          });
        }

        if (!invoiceDoc && currentUser) {
          await resolveInvoiceViaCallable();
        }

        if (!invoiceDoc) {
          adminLog('Invoice not found after all attempts', { invoiceId });
          
          // If we found an RSVP but couldn't read the invoice, it's a permission issue
          // This typically happens when non-logged-in users try to access registered user invoices
          if (foundRsvpButCannotReadInvoice) {
            setErrorType('permission-denied');
            showError('This invoice requires authentication. Please log in to pay.');
            setLoading(false);
            return;
          }
          
          // Show error but don't set invoice to null yet - let the UI handle it
          setErrorType('not-found');
          showError('Failed to load invoice');
          setLoading(false);
          return;
        }

        const invoiceData = invoiceDoc.data();
        
        // Extract unitId and eventId from the document path
        // Path format: units/{unitId}/events/{eventId}/invoices/{invoiceId}
        const pathParts = invoiceDoc.ref.path.split('/');
        unitId = pathParts[1];
        eventId = pathParts[3];

        // Invoice links may be shared in announcements and should stay publicly viewable.
        // Authorisation for mutating operations remains enforced in backend functions/rules.
        const isGuestPayment = !invoiceData.ownerUid || invoiceData.ownerUid === '';
        adminLog('Invoice access check', {
          hasOwnerUid: !!invoiceData.ownerUid,
          ownerUid: invoiceData.ownerUid,
          isGuestPayment,
          hasCurrentUser: !!currentUser,
          currentUserId: currentUser?.uid,
          isAdmin,
          accessMode: 'public-link',
        });

        // Check if invoice is voided
        if (invoiceData.status === 'voided') {
          setErrorType('voided');
          setInvoice(null);
          showError('This invoice has been voided and cannot be paid.');
          setLoading(false);
          return;
        }

        // Load invoice with proper typing
        const invoice = mapInvoiceFromPaymentSnapshot(invoiceDoc, unitId, eventId);

        setInvoice(invoice);
        setErrorType(null); // Clear any previous error when invoice loads successfully

        let breakdownResult: { event: Event; rsvps: RSVP[] } | null = null;
        const rsvpIds = Array.isArray(invoice.rsvps)
          ? invoice.rsvps.filter((id): id is string => typeof id === 'string' && id.length > 0)
          : [];
        if (rsvpIds.length > 0) {
          try {
            const eventSnap = await getDoc(
              doc(db, 'units', invoice.unitId, 'events', invoice.eventId),
            );
            if (eventSnap.exists()) {
              const mappedEvent = mapEventData(eventSnap, invoice.unitId);
              // Skip bookings this user can't read (e.g. a guest since moved to another invoice).
              const rsvpResults = await Promise.allSettled(
                rsvpIds.map((rid) =>
                  getDoc(doc(db, 'units', invoice.unitId, 'events', invoice.eventId, 'rsvps', rid)),
                ),
              );
              const loaded: RSVP[] = [];
              for (const result of rsvpResults) {
                if (result.status !== 'fulfilled') continue;
                const s = result.value;
                if (!s.exists()) continue;
                const r = mapRsvpData(s);
                if (r) loaded.push(r);
              }
              if (loaded.length > 0) {
                breakdownResult = { event: mappedEvent, rsvps: loaded };
              }
            }
          } catch (brErr) {
            adminLog('Could not load invoice line breakdown', {
              error: brErr instanceof Error ? brErr.message : String(brErr),
            });
          }
        }
        setPaymentBreakdown(breakdownResult);

        adminLog('Invoice loaded', {
          invoiceId: invoice.id,
          unitId: invoice.unitId,
          eventId: invoice.eventId,
          totalAmount: invoice.totalAmount,
          outstandingBalance: invoice.outstandingBalance,
          status: invoice.status,
        });

        // Fetch unit details for payment methods
        const unitDoc = await getDoc(doc(db, 'units', unitId));
        let available: PaymentMethodType[] = [];
        let bankDetails: typeof unitBankDetails = null;

        if (unitDoc.exists()) {
          const unitData = unitDoc.data();
          const paymentMethods = (unitData.settings?.paymentMethods || {}) as Record<string, any>;
          
          const sumupEnvironment = paymentMethods.sumup?.environment || 'not-set';
          adminLog('Unit payment methods', {
            unitId,
            sumupEnvironment: sumupEnvironment === 'production' ? '🔴 PRODUCTION' : sumupEnvironment === 'sandbox' ? '🟡 SANDBOX' : '❌ NOT SET',
            paymentMethods: {
              bankTransfer: {
                enabled: paymentMethods.bankTransfer?.enabled,
                configured: !!(paymentMethods.bankTransfer?.nameOnAccount || paymentMethods.bankTransfer?.sortCode),
              },
              stripe: {
                enabled: paymentMethods.stripe?.enabled,
                configured: !!unitData.stripeConnectedAccountId,
              },
              sumup: {
                enabled: paymentMethods.sumup?.enabled,
                configured: !!paymentMethods.sumup?.apiKeyEncrypted,
                environment: paymentMethods.sumup?.environment || 'not-set',
                merchantCode: paymentMethods.sumup?.merchantCode ? '***' + paymentMethods.sumup.merchantCode.slice(-4) : undefined,
              },
              slickpay: {
                enabled: paymentMethods.slickpay?.enabled,
                configured: isSlickPayBankDetailsConfigured(paymentMethods),
              },
            },
          });

          const bankTransfer = paymentMethods.bankTransfer;
          if (bankTransfer?.enabled && (bankTransfer.nameOnAccount || bankTransfer.sortCode || bankTransfer.accountNumber)) {
            bankDetails = {
              nameOnAccount: bankTransfer.nameOnAccount,
              sortCode: bankTransfer.sortCode,
              accountNumber: bankTransfer.accountNumber,
            };
          }

          available.push(...resolveInvoicePaymentMethods(unitData, paymentSettings));
          console.log('[SlickPay debug]', {
            platformEnabled: paymentSettings.slickpay.enabled,
            unitEnabled: paymentMethods.slickpay?.enabled,
            bankDetailsOk: isSlickPayBankDetailsConfigured(paymentMethods),
            verification: paymentMethods.slickpay?.registration?.status ?? 'untracked',
            slickpaySettings: paymentMethods.slickpay,
          });
        }

        available = Array.from(new Set(available));
        setUnitBankDetails(bankDetails);

        if (available.length === 0 && bankDetails) {
          available.push('bank_transfer');
        }

        setAvailableMethods(available);
        if (available.length > 0) {
          const defaultMethod = available.includes('slickpay') ? 'slickpay' : available[0]!;
          setSelectedMethod((prev) => (available.includes(prev) ? prev : defaultMethod));
        }
        
        adminLog('Available payment methods', {
          available,
          selectedMethod:
            available.length === 0
              ? '(none)'
              : available.includes(selectedMethod)
                ? selectedMethod
                : available[0],
        });

        // Default payment amount: matches RSVP dashboard (live pricing, not capped by stale outstanding)
        const payableDefault = getPayableOutstandingForInvoice(invoice, breakdownResult);
        if (payableDefault > 0) {
          setPaymentAmount(payableDefault.toString());
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching invoice:', error);
        adminLog('Error fetching invoice', {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
        showError('Failed to load invoice');
        setLoading(false);
      }
    };

    fetchInvoiceData();
  }, [invoiceId, currentUser]);

  useEffect(() => {
    if (!invoice?.unitId || !invoice?.eventId || !invoice?.id) return;

    const uid = invoice.unitId;
    const eid = invoice.eventId;
    const iid = invoice.id;

    // Guests cannot subscribe to the invoice document directly (reads are locked down). They loaded
    // the invoice via getInvoiceForPayment and rely on the provider callback pages for status; only
    // authenticated owners/admins/unit members get the live invoice listener (for other signed-in
    // payers the listener is denied and the callable summary stands).
    let unsubInv: (() => void) | undefined;
    if (currentUser) {
      const invRef = doc(db, 'units', uid, 'events', eid, 'invoices', iid);
      unsubInv = onSnapshot(invRef, (snap) => {
        if (!snap.exists()) {
          setInvoice(null);
          return;
        }
        const data = snap.data();
        if (data?.status === 'voided') {
          setErrorType('voided');
          setInvoice(null);
          return;
        }
        setInvoice(mapInvoiceFromPaymentSnapshot(snap, uid, eid));
      }, (error) => {
        console.warn('Live invoice updates unavailable:', error.code);
      });
    }

    const evRef = doc(db, 'units', uid, 'events', eid);
    const unsubEv = onSnapshot(evRef, (snap) => {
      if (!snap.exists()) return;
      const ev = mapEventData(snap, uid);
      setPaymentBreakdown((prev) => (prev ? { ...prev, event: ev } : prev));
    });

    return () => {
      if (unsubInv) unsubInv();
      unsubEv();
    };
  }, [invoice?.id, invoice?.unitId, invoice?.eventId, currentUser]);

  useEffect(() => {
    if (fullPayment && invoice && paymentBreakdown) {
      const payableDefault = getPayableOutstandingForInvoice(invoice, paymentBreakdown);
      if (payableDefault > 0) {
        setPaymentAmount(payableDefault.toString());
      }
    }
  }, [fullPayment, invoice, paymentBreakdown]);

  const handlePaymentAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPaymentAmount(value);
    
    if (invoice) {
      const payable = getPayableOutstandingForInvoice(invoice, paymentBreakdown);
      setFullPayment(parseFloat(value) >= payable);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!invoice) {
      showError('Invoice not found');
      return;
    }

    if (!isOnlineMethod) {
      showError('Please select an online payment method.');
      return;
    }

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount < 5) {
      showError('Minimum payment amount is £5');
      return;
    }

    const payable = getPayableOutstandingForInvoice(invoice, paymentBreakdown);
    if (amount > payable) {
      showError('Payment amount cannot exceed amount due');
      return;
    }

    if (invoice.status === 'voided') {
      showError('This invoice has been voided and cannot be paid.');
      return;
    }

    if (invoice.status === 'pending_verification') {
      showError(
        'A bank transfer is already awaiting approval. Please wait for the treasurer to confirm it before making another payment.',
      );
      return;
    }

    setProcessing(true);

    const checkoutProcessingFee = additiveProcessingFeeForCheckout(
      invoice,
      selectedMethod,
      processingFees
    );

    // Get SumUp environment for logging if SumUp is selected
    let sumupEnvironmentInfo = null;
    let slickpayEnvironmentInfo = null;
    if (selectedMethod === 'sumup') {
      const unitDoc = await getDoc(doc(db, 'units', invoice.unitId));
      if (unitDoc.exists()) {
        const unitData = unitDoc.data();
        const sumupConfig = unitData.settings?.paymentMethods?.sumup;
        sumupEnvironmentInfo = sumupConfig?.environment || 'not-set';
      }
    }
    if (selectedMethod === 'slickpay') {
      const unitDoc = await getDoc(doc(db, 'units', invoice.unitId));
      if (unitDoc.exists()) {
        const unitData = unitDoc.data();
        const slickConfig = unitData.settings?.paymentMethods?.slickpay;
        slickpayEnvironmentInfo = slickConfig?.environment || 'not-set';
      }
    }

    adminLog('Initiating payment', {
      method: selectedMethod,
      amount,
      processingFee: checkoutProcessingFee,
      invoiceId: invoice.id,
      unitId: invoice.unitId,
      eventId: invoice.eventId,
      outstandingBalance: invoice.outstandingBalance,
      availableMethods,
      ...(selectedMethod === 'sumup' && sumupEnvironmentInfo
        ? {
            sumupEnvironment:
              sumupEnvironmentInfo === 'production'
                ? '🔴 PRODUCTION'
                : sumupEnvironmentInfo === 'sandbox'
                  ? '🟡 SANDBOX'
                  : '❌ NOT SET',
          }
        : {}),
      ...(selectedMethod === 'slickpay' && slickpayEnvironmentInfo
        ? {
            slickpayEnvironment:
              slickpayEnvironmentInfo === 'production'
                ? '🔴 PRODUCTION'
                : slickpayEnvironmentInfo === 'sandbox'
                  ? '🟡 SANDBOX'
                  : '❌ NOT SET',
          }
        : {}),
      systemPaymentSettings,
    });
    
    try {
      const functionName =
        selectedMethod === 'sumup'
          ? 'createSumUpCheckoutSession'
          : selectedMethod === 'slickpay'
            ? 'createSlickPayCheckoutSession'
            : 'createStripeCheckoutSession';
      const createCheckoutSession = httpsCallable(functions, functionName);

      adminLog('Calling checkout function', {
        functionName,
        params: {
          unitId: invoice.unitId,
          eventId: invoice.eventId,
          invoiceId: invoice.id,
          amount,
          processingFee: checkoutProcessingFee,
        },
      });

      const result = await createCheckoutSession({
        unitId: invoice.unitId,
        eventId: invoice.eventId,
        invoiceId: invoice.id,
        amount,
        processingFee: checkoutProcessingFee,
      });

      adminLog('Checkout session created', {
        result: result.data,
        hasUrl: !!(result.data as any)?.url,
      });

      const data = result.data as { 
        url?: string; 
        totalAmount?: number; 
        processingFee?: number;
        checkoutId?: string;
        slickpayInvoiceId?: string;
        widgetRequired?: boolean;
        widgetScriptUrl?: string;
      };

      // Redirect to payment provider's hosted checkout page
      if (data.url) {
        adminLog('Redirecting to hosted checkout page', {
          url: data.url,
          totalAmount: data.totalAmount,
          method: selectedMethod,
        });
        
        // Update RSVP status to 'confirmed' before redirecting
        // This marks the RSVP as confirmed when payment is initiated
        if (invoice.rsvps && invoice.rsvps.length > 0) {
          try {
            adminLog('Updating RSVP status to confirmed', {
              rsvpIds: invoice.rsvps,
              unitId: invoice.unitId,
              eventId: invoice.eventId,
            });
            
            // Update all RSVPs associated with this invoice
            const updatePromises = invoice.rsvps.map((rsvpId: string) => {
              const rsvpRef = doc(
                db,
                'units',
                invoice.unitId,
                'events',
                invoice.eventId,
                'rsvps',
                rsvpId
              );
              return updateDoc(rsvpRef, {
                status: 'confirmed',
                updatedAt: Timestamp.now(),
              });
            });
            
            await Promise.all(updatePromises);
            
            adminLog('RSVP status updated successfully', {
              count: invoice.rsvps.length,
            });
          } catch (rsvpUpdateError: any) {
            // Log error but don't block the redirect
            // The payment can still proceed even if RSVP update fails
            adminLog('ERROR: Failed to update RSVP status', {
              error: rsvpUpdateError.message,
              code: rsvpUpdateError.code,
            });
            console.error('Error updating RSVP status:', rsvpUpdateError);
          }
        }
        
        // For SumUp, store checkoutId in localStorage so callback page can retrieve it
        if (selectedMethod === 'sumup' && invoiceId && data.checkoutId) {
          localStorage.setItem(`sumup_checkout_${invoiceId}`, data.checkoutId);
        }
        if (selectedMethod === 'slickpay' && invoiceId && data.slickpayInvoiceId) {
          localStorage.setItem(`slickpay_invoice_${invoiceId}`, data.slickpayInvoiceId);
          // Send the guest back to their RSVP dashboard for this event, not the invoice page,
          // once SlickPay confirms the Open Banking payment. Guests (no account) can only open the
          // dashboard with their capability token, which lives in sessionStorage — lost when the
          // bank app returns them in a new tab — so carry it in the return path. Without a token a
          // guest stays on the invoice page, which loads without one.
          const hostRsvpId = invoice.rsvps?.[0];
          const guestToken = hostRsvpId ? readStoredGuestRsvpToken(hostRsvpId) : null;
          const rsvpDashboardPath =
            hostRsvpId && (currentUser || guestToken)
              ? `/unit/${invoice.unitId}/event/${invoice.eventId}/rsvp/${hostRsvpId}/dashboard` +
                (guestToken ? `?t=${encodeURIComponent(guestToken)}` : '')
              : undefined;
          saveSlickPayReturnOrigin(slickPayInvoiceOriginKey(invoiceId), rsvpDashboardPath);
          savePendingSlickPayOrder(data.slickpayInvoiceId);
        }
        
        window.location.href = data.url;
      } else {
        adminLog('ERROR: No checkout URL returned', {
          resultData: result.data,
        });
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      
      adminGroupLog('Payment Error Details', {
        error: {
          message: error.message,
          code: error.code,
          details: error.details,
          stack: error.stack,
        },
        context: {
          method: selectedMethod,
          amount,
          processingFee: checkoutProcessingFee,
          invoiceId: invoice.id,
          unitId: invoice.unitId,
          eventId: invoice.eventId,
        },
      });
      
      Sentry.captureException(error, {
        tags: {
          area: 'payment',
          payment_method: String(selectedMethod),
          error_code: typeof error?.code === 'string' ? error.code : 'unknown',
        },
        extra: {
          invoiceId: invoice.id,
          unitId: invoice.unitId,
          eventId: invoice.eventId,
          amount,
          processingFee: checkoutProcessingFee,
        },
      });

      // Guests only see what they can act on. Open Banking set-up problems (bank details, Hope Macy
      // registration, provider errors) go to Sentry and the slickpayOrders audit instead.
      let displayMessage = error.message || 'Failed to initiate payment. Please try again.';
      if (selectedMethod === 'slickpay') {
        const msg = String(error.message || '').toLowerCase();
        if (msg.includes('already been paid')) {
          displayMessage = 'This invoice has already been paid.';
        } else if (msg.includes('voided') || msg.includes('cancelled')) {
          displayMessage = 'This invoice has been cancelled.';
        } else if (error.code !== 'functions/invalid-argument') {
          displayMessage = 'Open Banking is unavailable for this payment right now. Please choose another payment method or try again later.';
        }
      }
      showError(displayMessage);
      setProcessing(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!invoice) {
      showError('Invoice not found');
      return;
    }
    
    // Allow guest payments - check if invoice is a guest payment
    const isGuestPayment = !invoice.ownerUid || invoice.ownerUid === '';
    if (!isGuestPayment && !currentUser) {
      showError('You must be logged in to mark this payment as paid');
      return;
    }

    const result = await confirmAction({
      title: 'Mark Payment as Paid',
      text: 'Have you completed the bank transfer? This will set your payment as pending verification and notify the secretary and treasurer.',
      confirmButtonText: 'Yes, I have paid',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    const claimedAmount = getPayableOutstandingForInvoice(invoice, paymentBreakdown);
    if (claimedAmount <= 0) {
      showError('There is no outstanding balance to mark as paid.');
      return;
    }

    setMarkingPaid(true);
    try {
      // notifyPaymentMade sets the invoice to pending_verification server-side (guests can no longer
      // write invoices directly), then notifies the secretary/treasurer.
      const notifyPaymentMade = httpsCallable(functions, 'notifyPaymentMade');
      
      await notifyPaymentMade({
        unitId: invoice.unitId,
        eventId: invoice.eventId,
        invoiceId: invoice.id,
        userId: currentUser?.uid || invoice.ownerUid || '',
        amount: claimedAmount,
      });

      // Update local state
      setInvoice({
        ...invoice,
        status: 'pending_verification',
        pendingVerificationAmount: claimedAmount,
        updatedAt: new Date(),
      });

      showSuccess('Payment marked as pending verification! The secretary and treasurer will review your payment.');
    } catch (error: any) {
      console.error('Error marking payment as pending verification:', error);
      showError(error.message || 'Failed to update payment status. Please try again.');
    } finally {
      setMarkingPaid(false);
    }
  };

  const copyBankDetails = () => {
    if (!unitBankDetails) return;
    
    const details = [
      unitBankDetails.nameOnAccount && `Name on Account: ${unitBankDetails.nameOnAccount}`,
      unitBankDetails.sortCode && `Sort Code: ${unitBankDetails.sortCode}`,
      unitBankDetails.accountNumber && `Account Number: ${unitBankDetails.accountNumber}`,
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(details);
    showSuccess('Bank details copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading payment details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>
                {errorType === 'permission-denied' 
                  ? 'Authentication Required' 
                  : errorType === 'voided'
                  ? 'Invoice Voided'
                  : 'Invoice Not Found'}
              </CardTitle>
              <CardDescription>
                {errorType === 'permission-denied'
                  ? 'This invoice requires authentication. Please log in to pay.'
                  : errorType === 'voided'
                  ? 'This invoice has been voided and cannot be paid. Please contact support if you have questions.'
                  : "The invoice you're looking for doesn't exist or you don't have access."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const displayInvoice = invoice;
  const isOnlineMethod =
    selectedMethod === 'stripe' || selectedMethod === 'sumup' || selectedMethod === 'slickpay';
  const additiveProcessingFee = additiveProcessingFeeForCheckout(
    displayInvoice,
    selectedMethod,
    processingFees
  );

  const hostedCheckoutBrand =
    selectedMethod === 'sumup' ? 'SumUp' : selectedMethod === 'slickpay' ? 'SlickPay' : 'Stripe';

  const breakdownOrdered =
    paymentBreakdown && displayInvoice
      ? orderRsvpsForInvoice(displayInvoice, paymentBreakdown.rsvps)
      : [];
  const breakdownBillable =
    paymentBreakdown && displayInvoice
      ? billableRsvpsForInvoicePayer(displayInvoice, paymentBreakdown.rsvps)
      : [];
  const breakdownEvent = paymentBreakdown?.event;
  const invoiceLineCount = breakdownBillable.length;
  const billableCount =
    breakdownEvent && breakdownBillable.length > 0
      ? countBookingFeeBillableSlots(breakdownBillable, breakdownEvent, invoiceLineCount)
      : 0;
  const billableTicketSum =
    breakdownEvent && breakdownBillable.length > 0
      ? sumBookingFeeBillableTicketSubtotal(breakdownBillable, breakdownEvent, invoiceLineCount)
      : 0;
  const breakdownFeePence = breakdownEvent ? getCreditBookingFeePenceForEvent(breakdownEvent) : 0;
  const breakdownRecoveryTotal =
    breakdownEvent && billableCount > 0
      ? computeCreditRecoveryTotalPounds(
          breakdownEvent,
          billableTicketSum,
          billableCount,
          breakdownFeePence,
        )
      : 0;
  const breakdownPerSlotBooking =
    breakdownEvent && billableCount > 0
      ? perSlotBookingFeeForBillableTotals(
          breakdownEvent,
          billableTicketSum,
          billableCount,
          breakdownFeePence,
        )
      : 0;
  const payableOutstanding = getPayableOutstandingForInvoice(displayInvoice, paymentBreakdown);
  const displayAmount = fullPayment ? payableOutstanding : (parseFloat(paymentAmount) || 0);
  const totalWithFee = displayAmount + additiveProcessingFee;

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold">Secure Payment</h1>
                  {displayInvoice.invoiceNumber && (
                    <Badge variant="outline" className="text-sm">
                      Invoice: {displayInvoice.invoiceNumber}
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground">
                  {availableMethods.length === 0
                    ? 'This unit is not using online payment for this invoice. Use the information below about paying in person.'
                    : PAYMENT_METHOD_META[selectedMethod]?.hero || 'Select your preferred payment method below'}
                </p>
              </div>
              {invoice && invoice.rsvps && invoice.rsvps.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/unit/${invoice.unitId}/event/${invoice.eventId}/rsvp/${invoice.rsvps[0]}/dashboard`)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to RSVP
                </Button>
              )}
            </div>
          </div>

          {availableMethods.length > 0 && (
            <div className="mb-8">
              <p className="mb-3 text-sm font-medium text-muted-foreground">Select payment method</p>
              <div className={cn(
                'grid gap-4',
                availableMethods.length === 1 ? 'grid-cols-1 max-w-sm' :
                availableMethods.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
                availableMethods.length === 3 ? 'grid-cols-1 sm:grid-cols-3' :
                'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'
              )}>
                {availableMethods.map((method) => {
                  const meta = PAYMENT_METHOD_META[method];
                  const Icon = meta.icon;
                  const isSelected = selectedMethod === method;
                  return (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setSelectedMethod(method)}
                      className={cn(
                        'group flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all duration-200',
                        isSelected
                          ? 'border-primary bg-primary/5 shadow-md ring-1 ring-primary/20'
                          : 'border-border bg-card hover:border-primary/60 hover:shadow-sm'
                      )}
                    >
                      <div className={cn(
                        'rounded-xl p-3 transition-colors shrink-0',
                        isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
                      )}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <span className={cn(
                        'flex-1 font-semibold text-base leading-tight',
                        isSelected ? 'text-primary' : 'text-foreground'
                      )}>{meta.label}</span>
                      {isSelected && (
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary">
                          <svg className="h-3 w-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div
            className={cn(
              'grid gap-6',
              displayInvoice.paymentHistory.length > 0 && 'lg:grid-cols-3'
            )}
          >
            {/* Payment Form */}
            <div className={cn(displayInvoice.paymentHistory.length > 0 && 'lg:col-span-2')}>
              {sumupWidget?.show ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Smartphone className="mr-2 h-5 w-5" />
                      SumUp Payment
                    </CardTitle>
                    <CardDescription>
                      Complete your payment securely using the form below
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div id="sumup-widget-container" className="min-h-[400px]">
                      <div className="flex items-center justify-center py-8">
                        <div className="text-center">
                          <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
                          <p className="text-muted-foreground">Loading payment form...</p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSumupWidget(null);
                          setProcessing(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : availableMethods.length === 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Banknote className="h-5 w-5" />
                      Pay in person on the night
                    </CardTitle>
                    <CardDescription>
                      Online payment is not available for this booking
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      This unit has not enabled any online payment methods (such as bank transfer or card checkout) for
                      this event, so you cannot use this page to pay remotely.
                    </p>
                    <p className="text-muted-foreground">
                      Please pay by <strong>cash or card on the night</strong> of the event. If you are not sure, contact
                      the unit for details.
                    </p>
                    <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-4 text-sm">
                      <Banknote className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />
                      <p className="font-medium text-foreground">Cash or card on the night at the event</p>
                    </div>
                  </CardContent>
                </Card>
              ) : isOnlineMethod ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <CreditCard className="mr-2 h-5 w-5" />
                      Payment Amount
                    </CardTitle>
                    <CardDescription>
                      Select the amount you'd like to pay
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Payment Amount */}
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <input
                          type="radio"
                          id="fullPayment"
                          checked={fullPayment}
                          onChange={() => {
                            setFullPayment(true);
                            setPaymentAmount(getPayableOutstandingForInvoice(displayInvoice, paymentBreakdown).toString());
                          }}
                          className="h-4 w-4"
                        />
                        <Label htmlFor="fullPayment" className="cursor-pointer">
                          Pay full amount ({formatCurrency(payableOutstanding + additiveProcessingFee)})
                        </Label>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <input
                          type="radio"
                          id="partialPayment"
                          checked={!fullPayment}
                          onChange={() => setFullPayment(false)}
                          className="h-4 w-4"
                        />
                        <Label htmlFor="partialPayment" className="cursor-pointer">
                          Pay partial amount
                        </Label>
                      </div>

                      {!fullPayment && (
                        <div className="ml-8 space-y-2">
                          <Label htmlFor="paymentAmount">Amount to Pay (£) *</Label>
                          <Input
                            id="paymentAmount"
                            type="number"
                            step="0.01"
                            min="5"
                            max={payableOutstanding}
                            placeholder="Enter amount"
                            value={paymentAmount}
                            onChange={handlePaymentAmountChange}
                            required
                          />
                          <p className="text-xs text-muted-foreground">
                            Minimum payment: £5.00
                          </p>
                        </div>
                      )}
                    </div>

                    {breakdownBillable.length > 0 && breakdownEvent && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <h3 className="text-sm font-semibold text-foreground">Invoice breakdown</h3>
                          <p className="text-xs text-muted-foreground">
                            {breakdownPerSlotBooking > 0 ? (
                              <>
                                The organiser has passed credit metering costs to guests. Each paying seat below includes the{' '}
                                {BOOKING_FEE_LABEL.toLowerCase()} ({formatCurrency(breakdownPerSlotBooking)} per person)
                                set for this event.
                              </>
                            ) : (
                              <>
                                Ticket prices are shown per guest. No {BOOKING_FEE_LABEL.toLowerCase()} applies on this
                                invoice under the current event rules, or ticket totals are zero.
                              </>
                            )}
                          </p>
                          {(breakdownOrdered.length !== displayInvoice.rsvps.length ||
                            breakdownBillable.length !== breakdownOrdered.length) && (
                            <p className="text-xs text-amber-800 dark:text-amber-200">
                              Ticket and fee totals reflect guests on this bill only (stale or unloaded
                              invoice links are excluded).
                            </p>
                          )}
                        </div>
                        <div className="overflow-x-auto rounded-md border border-border">
                          <table className="w-full min-w-[320px] text-sm">
                            <thead>
                              <tr className="border-b border-border bg-muted/60 text-left">
                                <th className="p-2 font-medium">Guest</th>
                                <th className="p-2 font-medium">Ticket</th>
                                <th className="p-2 font-medium text-right">Ticket price</th>
                                <th className="p-2 font-medium text-right">{BOOKING_FEE_LABEL}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {breakdownBillable.map((r) => {
                                const guestLabel = [r.rank, formatRsvpGuestName(r)].filter(Boolean).join(' ').trim();
                                const ticketLabel = describeRsvpTicketSelection(r, breakdownEvent);
                                const ticketPrice = ticketSubtotalForBreakdownRow(r, breakdownEvent);
                                return (
                                  <tr key={r.id} className="border-b border-border last:border-0">
                                    <td className="p-2 align-top">{guestLabel || formatRsvpGuestName(r)}</td>
                                    <td className="p-2 align-top text-muted-foreground">{ticketLabel}</td>
                                    <td className="p-2 align-top text-right tabular-nums">
                                      {formatCurrency(ticketPrice)}
                                    </td>
                                    <td className="p-2 align-top text-right tabular-nums">
                                      {breakdownPerSlotBooking > 0 &&
                                      isBookingFeeBillableInvoiceRow(r, breakdownEvent, invoiceLineCount)
                                        ? formatCurrency(breakdownPerSlotBooking)
                                        : formatCurrency(0)}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr className="border-t border-border bg-muted/40 font-medium">
                                <td className="p-2" colSpan={2}>
                                  Totals
                                </td>
                                <td className="p-2 text-right tabular-nums">{formatCurrency(billableTicketSum)}</td>
                                <td className="p-2 text-right tabular-nums">
                                  {formatCurrency(breakdownRecoveryTotal)}
                                </td>
                              </tr>
                              <tr className="border-t border-border bg-muted/40">
                                <td className="p-3 text-base font-medium text-foreground" colSpan={4}>
                                  Outstanding Balance:{' '}
                                  <span className="text-lg font-semibold tabular-nums">
                                    {formatCurrency(payableOutstanding + additiveProcessingFee)}
                                  </span>
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}

                    {additiveProcessingFee > 0 && (
                      <div className="border-t pt-6">
                        <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/40">
                          <div className="flex items-start">
                            <DollarSign className="mr-2 mt-0.5 h-5 w-5 text-blue-600 dark:text-blue-300" />
                            <div className="flex-1">
                              <h3 className="font-semibold text-blue-900 dark:text-blue-100">Card processing fee</h3>
                              <p className="mt-1 text-sm text-blue-800 dark:text-blue-200">
                                A card processing fee of {formatCurrency(additiveProcessingFee)} will be added by the
                                payment provider.
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-between border-t border-blue-200 pt-3 text-sm dark:border-blue-900">
                            <span className="text-blue-900 dark:text-blue-100">Payment Amount:</span>
                            <span className="font-medium text-blue-900 dark:text-blue-100">{formatCurrency(displayAmount)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-blue-900 dark:text-blue-100">Card processing fee:</span>
                            <span className="font-medium text-blue-900 dark:text-blue-100">{formatCurrency(additiveProcessingFee)}</span>
                          </div>
                          <div className="flex justify-between border-t border-blue-200 pt-3 dark:border-blue-900">
                            <span className="font-bold text-blue-900 dark:text-blue-100">Total:</span>
                            <span className="text-lg font-bold text-blue-900 dark:text-blue-100">{formatCurrency(totalWithFee)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm dark:border-green-900 dark:bg-green-950/40">
                      <Lock className="h-4 w-4 text-green-600 dark:text-green-300" />
                      <p className="text-green-900 dark:text-green-100">
                        Your payment is secure. You&apos;ll be redirected to {hostedCheckoutBrand}&apos;s secure checkout page.
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full" 
                      size="lg" 
                      disabled={processing || (!fullPayment && !paymentAmount)}
                    >
                      {processing ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Redirecting to {hostedCheckoutBrand}...
                        </>
                      ) : (
                        <>Continue to {hostedCheckoutBrand} checkout</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Building2 className="mr-2 h-5 w-5" />
                      Bank Transfer Payment
                    </CardTitle>
                    <CardDescription>
                      Complete your payment via bank transfer
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {unitBankDetails ? (
                      <>
                        <div className="space-y-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/40">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-blue-900 dark:text-blue-100">Bank Transfer Details</h3>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={copyBankDetails}
                              className="flex items-center gap-2"
                            >
                              <Copy className="h-4 w-4" />
                              Copy
                            </Button>
                          </div>
                          <div className="space-y-3">
                            {unitBankDetails.nameOnAccount && (
                              <div>
                                <Label className="text-sm text-blue-800 dark:text-blue-200">Name on Account</Label>
                                <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">{unitBankDetails.nameOnAccount}</div>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                              {unitBankDetails.sortCode && (
                                <div>
                                  <Label className="text-sm text-blue-800 dark:text-blue-200">Sort Code</Label>
                                  <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">{unitBankDetails.sortCode}</div>
                                </div>
                              )}
                              {unitBankDetails.accountNumber && (
                                <div>
                                  <Label className="text-sm text-blue-800 dark:text-blue-200">Account Number</Label>
                                  <div className="text-lg font-semibold text-blue-900 dark:text-blue-100">{unitBankDetails.accountNumber}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-900 dark:bg-yellow-950/40">
                          <h4 className="mb-2 font-semibold text-yellow-900 dark:text-yellow-100">Important Instructions</h4>
                          <ul className="list-inside list-disc space-y-1 text-sm text-yellow-800 dark:text-yellow-200">
                            <li>Please use <strong>{displayInvoice.invoiceNumber || displayInvoice.id}</strong> as the payment reference</li>
                            <li>Transfer the exact amount: <strong>{formatCurrency(payableOutstanding)}</strong></li>
                            <li>Payment processing may take 1-3 business days</li>
                            <li>Once you've completed the transfer, click "Mark as Paid" below</li>
                          </ul>
                        </div>

                        <div className="border-t pt-4">
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Amount to Pay:</span>
                              <span className="font-semibold text-lg">{formatCurrency(payableOutstanding)}</span>
                            </div>
                          </div>
                        </div>

                        <Button
                          onClick={handleMarkAsPaid}
                          className="w-full"
                          size="lg"
                          disabled={markingPaid || payableOutstanding === 0 || displayInvoice.status === 'pending_verification' || displayInvoice.status === 'voided'}
                        >
                          {markingPaid ? (
                            <>
                              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                              Sending Notification...
                            </>
                          ) : displayInvoice.status === 'pending_verification' ? (
                            <>
                              <Clock className="mr-2 h-4 w-4" />
                              Payment Pending Verification
                            </>
                          ) : (
                            <>
                              <CheckCircle className="mr-2 h-4 w-4" />
                              I Have Paid via Bank Transfer
                            </>
                          )}
                        </Button>
                        {displayInvoice.status === 'pending_verification' && (
                          <p className="text-sm text-muted-foreground text-center mt-2">
                            Your payment is pending verification. The secretary and treasurer will review it shortly.
                          </p>
                        )}
                        {displayInvoice.status === 'voided' && (
                          <p className="text-sm text-destructive text-center mt-2">
                            This invoice has been voided and cannot be paid. Please contact support if you have questions.
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <Building2 className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground">Bank transfer details are not configured for this unit.</p>
                        <p className="text-sm text-muted-foreground mt-2">Please contact the unit secretary for payment instructions.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {displayInvoice.paymentHistory.length > 0 && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-base">
                      <History className="mr-2 h-4 w-4" />
                      Payment History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {displayInvoice.paymentHistory.map((payment, index) => (
                        <div key={index} className="flex items-start justify-between text-sm">
                          <div>
                            <div className="font-medium">{formatCurrency(payment.amount)}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatDate(payment.date)}
                            </div>
                            {payment.processingFee > 0 && (
                              <div className="text-xs text-muted-foreground">
                                Fee: {formatCurrency(payment.processingFee)}
                              </div>
                            )}
                          </div>
                          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
